/**
 * Error Tracking Configuration
 *
 * This module provides integration with Sentry for error tracking.
 * Set SENTRY_DSN environment variable to enable error tracking.
 *
 * Installation:
 *   npm install @sentry/vue @sentry/tracing
 *
 * Environment Variables:
 *   SENTRY_DSN - Your Sentry DSN (required to enable)
 *   SENTRY_ENVIRONMENT - Environment name (defaults to NODE_ENV)
 *   SENTRY_RELEASE - Release version (defaults to package version)
 *   SENTRY_TRACES_SAMPLE_RATE - Performance tracing sample rate (0-1, default 0.1)
 */

interface SentryConfig {
  dsn: string;
  environment: string;
  release?: string;
  tracesSampleRate: number;
  enabled: boolean;
}

/**
 * Get Sentry configuration from environment
 */
export function getSentryConfig(): SentryConfig {
  let dsn = "";
  let environment = "development";

  try {
    const config = useRuntimeConfig();
    if (config.public?.sentryDsn) {
      dsn = config.public.sentryDsn;
    }
    if (config.public?.sentryEnvironment) {
      environment = config.public.sentryEnvironment;
    }
  } catch (e) {
    // Fallback to process.env if useRuntimeConfig is not available
    dsn = process.env.SENTRY_DSN || "";
    environment =
      process.env.SENTRY_ENVIRONMENT || process.env.NODE_ENV || "development";
  }

  const enabled = !!dsn && process.env.NODE_ENV === "production";

  return {
    dsn,
    enabled,
    environment,
    release: process.env.SENTRY_RELEASE,
    tracesSampleRate: parseFloat(
      process.env.SENTRY_TRACES_SAMPLE_RATE || "0.1",
    ),
  };
}

/**
 * Initialize Sentry for the frontend (Vue/Nuxt)
 * Call this in a Nuxt plugin
 *
 * @example
 * // plugins/sentry.client.ts
 * import { initSentryClient } from '~/utils/error-tracking';
 *
 * export default defineNuxtPlugin((nuxtApp) => {
 *   initSentryClient(nuxtApp.vueApp);
 * });
 */
export async function initSentryClient(app: any, dsn?: string) {
  const isDev = process.env.NODE_ENV !== "production";

  // Get config from runtimeConfig
  let finalDsn = dsn || "";
  let environment = "development";

  try {
    const runtimeConfig = useRuntimeConfig();
    if (!finalDsn && runtimeConfig.public?.sentryDsn) {
      finalDsn = runtimeConfig.public.sentryDsn;
    }
    if (runtimeConfig.public?.sentryEnvironment) {
      environment = runtimeConfig.public.sentryEnvironment;
    }
  } catch (e) {
    // Fallback
    finalDsn = dsn || process.env.SENTRY_DSN || "";
    environment =
      process.env.SENTRY_ENVIRONMENT || process.env.NODE_ENV || "development";
  }

  // Only enable in production
  const isProduction = process.env.NODE_ENV === "production";
  const enabled = !!finalDsn && isProduction;

  const config = {
    dsn: finalDsn,
    enabled,
    environment,
    release: process.env.SENTRY_RELEASE,
    tracesSampleRate: parseFloat(
      process.env.SENTRY_TRACES_SAMPLE_RATE || "0.1",
    ),
  };

  if (isDev) {
    console.log("[Sentry] Config:", {
      enabled: config.enabled,
      dsn: config.dsn ? config.dsn.substring(0, 50) + "..." : "none",
      environment: config.environment,
      isProduction,
    });
  }

  if (!config.enabled) {
    if (isDev) {
      console.log(
        "[Sentry] Disabled - only active in production with DSN configured",
      );
    }
    return;
  }

  try {
    if (isDev) {
      console.log("[Sentry] Attempting to initialize...");
    }
    // Regular import since @sentry/vue is installed
    const Sentry = await import("@sentry/vue");

    Sentry.init({
      app,
      dsn: config.dsn,
      environment: config.environment,
      release: config.release,

      // Performance Monitoring
      tracesSampleRate: config.tracesSampleRate,

      // Session Replay (if needed)
      // replaysSessionSampleRate: 0.1,
      // replaysOnErrorSampleRate: 1.0,

      // Filter out non-error events
      beforeSend(event: any, hint: any) {
        // Don't send events for network errors (handled separately)
        const error = hint.originalException;
        if (error instanceof Error) {
          // Filter out common non-critical errors
          if (error.message.includes("Network request failed")) {
            return null;
          }
          if (error.message.includes("Load failed")) {
            return null;
          }
          // Filter out WebKit/iOS Safari's service-worker registration failure.
          // Unlike Chrome ("Failed to fetch"), WebKit throws:
          // "TypeError: Script https://.../sw.js load failed" when
          // navigator.serviceWorker.register() can't fetch the SW script
          // (e.g. offline, flaky network, or mid-deploy). Harmless — same
          // root cause as the "Failed to fetch" case already filtered below.
          if (
            error.message.includes("load failed") &&
            (error.message.includes("/sw.js") ||
              error.message.includes("sw.js"))
          ) {
            return null;
          }
          // Nuxt useAsyncData deduplication cancels duplicate requests on
          // navigation — this is expected behaviour, not a real error.
          if (
            error.name === "AbortError" &&
            error.message.includes("deduplication")
          ) {
            return null;
          }
          // Filter out service-worker update fetch failures (user offline during
          // periodic SW update check — workbox-window throws an unhandled rejection).
          // Check stack frames first (most reliable), then fall back to breadcrumbs.
          if (error.message === "Failed to fetch") {
            const frames =
              event.exception?.values?.[0]?.stacktrace?.frames || [];
            const hasPwaFrame = frames.some(
              (frame: any) =>
                frame.module?.includes("vite-pwa") ||
                frame.filename?.includes("vite-pwa") ||
                frame.filename?.includes("pwa.client"),
            );
            if (hasPwaFrame) return null;

            const breadcrumbs: any[] = Array.isArray(event.breadcrumbs?.values)
              ? event.breadcrumbs.values
              : [];
            const hasSwFetchError = breadcrumbs.some(
              (b: any) =>
                b.category === "fetch" &&
                typeof b.data?.url === "string" &&
                (b.data.url.includes("/sw.js") ||
                  b.data.url.includes("/_nuxt/builds/latest.json")),
            );
            if (hasSwFetchError) return null;
          }
          // Filter out Chrome SW InvalidStateError — fired when registration.update()
          // races with navigation or tab discard. Harmless browser-internal error.
          if (
            error.name === "InvalidStateError" &&
            error.message.includes("ServiceWorker") &&
            error.message.includes("invalid state")
          ) {
            return null;
          }
          // Filter out PDF.js/flipbook canvas and audio errors
          if (error.message.includes("InvalidStateError")) {
            return null;
          }
          if (error.message.includes("NotAllowedError")) {
            return null;
          }
          if (
            error.message.includes("InvalidPDFException") ||
            error.message.includes("Invalid PDF structure")
          ) {
            return null;
          }
          if (
            error.message.includes("canvas") ||
            error.message.includes("transparentCanvas")
          ) {
            return null;
          }
          if (
            error.message.includes("audio") ||
            error.message.includes("play()")
          ) {
            return null;
          }
          // Filter out three.js property configuration errors
          if (
            error.message.includes("unconfigurable property") ||
            error.message.includes("THREE.WebGLRenderTarget")
          ) {
            return null;
          }
          // Filter out intentional control flow errors
          if (error.message.includes("POPUP_CLOSING")) {
            return null;
          }
          // Filter out Android WebView / Facebook in-app browser postMessage crash.
          // "Java object is gone" = native Java bridge GC'd while JS calls postMessage.
          // Not our code — Facebook's WebView internals.
          if (error.message.includes("Java object is gone")) {
            return null;
          }
          // Filter out Facebook/Android WebView JS bundle truncation.
          // FB in-app browser aggressively limits memory and can cut off a JS
          // chunk mid-stream, causing the parser to throw SyntaxError with
          // "Unexpected end of input". Not our code — environmental noise.
          if (
            error instanceof SyntaxError &&
            error.message.includes("Unexpected end of input")
          ) {
            const ua = navigator?.userAgent || "";
            if (
              ua.includes("FB_IAB") ||
              ua.includes("FBAV") ||
              ua.includes("FB4A")
            ) {
              return null;
            }
          }
        }

        // Filter out errors from flipbook scripts
        if (event.exception?.values) {
          const frames = event.exception.values[0]?.stacktrace?.frames || [];
          const hasFlipbookFrame = frames.some(
            (frame: any) =>
              frame.filename?.includes("/flipbook/") ||
              frame.filename?.includes("pdf.min.js") ||
              frame.filename?.includes("three.min.js") ||
              frame.filename?.includes("3dflipbook"),
          );
          if (hasFlipbookFrame) {
            return null;
          }
          // Filter out Vue SSR hydration mismatch crash in isMismatchAllowed.
          // Happens when bots (HeadlessChrome) modify the DOM before hydration,
          // causing Vue to call el.hasAttribute() on a non-Element node.
          // This is a Vue internal bug, not an app error.
          const hasVueMismatchFrame = frames.some(
            (frame: any) =>
              frame.filename?.includes("runtime-core.esm-bundler") &&
              frame.function === "isMismatchAllowed",
          );
          if (hasVueMismatchFrame) {
            return null;
          }
        }

        return event;
      },

      // Ignore certain URLs
      denyUrls: [
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        // Firefox extensions
        /^moz-extension:\/\//i,
      ],
    });

    if (isDev) {
      console.log("[Sentry] Initialized for frontend");
    }
  } catch (error) {
    if (isDev) {
      console.warn("[Sentry] Failed to initialize:", error);
    }
  }
}

/**
 * Capture an exception manually
 */
export async function captureException(
  error: Error,
  context?: Record<string, any>,
) {
  const isDev = process.env.NODE_ENV !== "production";
  const config = getSentryConfig();

  if (!config.enabled) {
    if (isDev) {
      console.error("[Error]", error, context);
    }
    return;
  }

  try {
    const sentryModule = "@sentry" + "/vue";
    const Sentry = await import(/* @vite-ignore */ sentryModule);
    Sentry.captureException(error, {
      extra: context,
    });
  } catch {
    if (isDev) {
      console.error("[Error]", error, context);
    }
  }
}

/**
 * Capture a message manually
 */
export async function captureMessage(
  message: string,
  level: "info" | "warning" | "error" = "info",
) {
  const isDev = process.env.NODE_ENV !== "production";
  const config = getSentryConfig();

  if (!config.enabled) {
    if (isDev) {
      console.log(`[${level}]`, message);
    }
    return;
  }

  try {
    const sentryModule = "@sentry" + "/vue";
    const Sentry = await import(/* @vite-ignore */ sentryModule);
    Sentry.captureMessage(message, level);
  } catch {
    if (isDev) {
      console.log(`[${level}]`, message);
    }
  }
}

/**
 * Set user context for error tracking
 */
export async function setUser(
  user: { id: string; email?: string; role?: string } | null,
) {
  const config = getSentryConfig();

  if (!config.enabled) {
    return;
  }

  try {
    const sentryModule = "@sentry" + "/vue";
    const Sentry = await import(/* @vite-ignore */ sentryModule);
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        // Don't include sensitive data
      });
    } else {
      Sentry.setUser(null);
    }
  } catch {
    // Ignore
  }
}

/**
 * Add breadcrumb for debugging
 */
export async function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, any>,
) {
  const config = getSentryConfig();

  if (!config.enabled) {
    return;
  }

  try {
    const sentryModule = "@sentry" + "/vue";
    const Sentry = await import(/* @vite-ignore */ sentryModule);
    Sentry.addBreadcrumb({
      message,
      category,
      data,
      level: "info",
    });
  } catch {
    // Ignore
  }
}
